import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { WaitlistEntry, WaitlistEntryStatus } from '../../../../../../../../../../../models/scheduled-event';
import { isGuardianWithEmail } from '../../../../../../../../../../../models/customer';
import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  getAge,
  timeStringFromISO8601
} from '../../../../../../../../../../../utils';
import { ScheduledEventService } from '../../../../../../../../../../../services/scheduled-event/scheduled-event.service';
import { EventService } from '../../../../../../../../../../../services/event/event.service';
import { Roster } from '../../../../../../../../../../../models/event-type';
import { EventTypeService } from '../../../../../../../../../../../services/event-type/event-type.service';
import { CustomerService } from '../../../../../../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../../../../../../services/organization-config/organization-config.service';


@Component({
  selector: 'app-invite-waitlist-entry-to-roster-modal',
  templateUrl: './invite-waitlist-entry-to-roster-modal.component.html',
  styleUrls: ['./invite-waitlist-entry-to-roster-modal.component.css']
})
export class InviteWaitlistEntryToRosterModalComponent implements OnInit {

  @Input() waitlistEntry: WaitlistEntry;
  @Input() inviteSentCallback: (result: CallbackResult) => undefined;

  public loadingRosters = true;
  public loadingSubmit = false;

  // Only used if this is an event type-wide waitlist (scheduledEvent === null).
  public rosters: Roster[] = [];
  public selectedRoster: Roster = null;
  public startDate = '';

  // The optional booking deadline. Left blank, a re-send keeps the entry's
  // current deadline and a first send uses the event type's acceptance window.
  public newDeadlineDate = '';
  public newDeadlineTime = '';

  // The note that will be included in the invitation email. Prefilled from the
  // entry so a re-send repeats the note the participant was originally sent;
  // staff can edit or empty it.
  public inviteNote = '';

  // Whether the invitation should also go to the participant's linked parents
  // and guardians. Asked on each send rather than remembered: the entry has no
  // record of what the last invitation chose.
  public sendInviteToParentsAndGuardians = false;

  // Whether any linked parent or guardian has an email address. Null while
  // unknown -- not looked up (the entry already has a recipient, so it does not
  // matter) or the lookup failed.
  public guardianWithEmailExists: boolean | null = null;
  public loadingRelations = false;

  private relationsRequestCount = 0;

  // True when the participant is too young to own a membership themselves, in
  // which case a guardian has to act on the invitation and the box above is
  // forced on -- matching the "Invite to Roster" modal.
  public participantBelowMinAge = false;

  public errorMessage = '';
  public serverError: number = null;

  public readonly WaitlistEntryStatus = WaitlistEntryStatus;

  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public activeModal: NgbActiveModal,
    private scheduledEventService: ScheduledEventService,
    private eventService: EventService,
    private eventTypeService: EventTypeService,
    private customerService: CustomerService,
    private orgService: OrganizationConfigService,
  ) {}

  ngOnInit() {
    // Start from the entry's own start date so that sending the invitation
    // again leaves it as staff set it (PRO-1489).
    this.startDate = this.waitlistEntry.start_date || '';
    this.inviteNote = this.waitlistEntry.invite_note || '';

    this.checkParticipantAge();
    this.loadGuardianEmailAvailability();

    this.loadRosters();
  }

  /**
   * Whether this is a re-send of an invitation the participant already has,
   * rather than a first invitation off the waitlist.
   */
  public isResend(): boolean {
    return (this.waitlistEntry.status === WaitlistEntryStatus.pending);
  }

  public handleClearStartDate() {
    this.startDate = '';
  }

  /**
   * Whether staff have removed the start date the entry came in with. Derived
   * rather than flagged by the Clear link, so deleting the text by hand means
   * the same as clicking Clear.
   */
  public startDateCleared(): boolean {
    return (!this.startDate && !!this.waitlistEntry.start_date);
  }

  /**
   * The addresses this invitation will reach, in the order the email helper
   * assembles them (mirrors `invite_waitlist_entry_to_book`). Guardians are
   * not listed: they are a per-send choice and their addresses are not on
   * the entry.
   */
  public emailRecipients(): string[] {
    if (this.participantIsRemoved()) { return []; }

    const entryAddresses = [
      this.waitlistEntry.notification_email,
      this.waitlistEntry.booking_customer_email
    ].filter(
      (email, index, all) => (!!email && (all.indexOf(email) === index))
    );

    // The participant's own address is a fallback, not an addition: the
    // endpoint only asks for it when the entry has no address of its own. See
    // invite_waitlist_entry_to_book.
    if (entryAddresses.length > 0) { return entryAddresses; }

    return this.waitlistEntry.participant_customer_email
      ? [this.waitlistEntry.participant_customer_email]
      : [];
  }

  /**
   * Whether this invitation has no address to go to. With no recipient the
   * email helper returns without sending, so the invite succeeds silently;
   * staff are warned rather than blocked.
   */
  public hasNoEmailRecipient(): boolean {
    // Before the guardians clause: a removed participant stops the helper
    // before it would add a guardian's address either.
    if (this.participantIsRemoved()) { return true; }

    if (this.emailRecipients().length > 0) { return false; }

    // Only claim nothing will be sent when we know no guardian can receive it.
    // A failed or pending lookup leaves this null, and an unproven warning is
    // worse than none.
    if (this.sendInviteToParentsAndGuardians &&
        (this.guardianWithEmailExists !== false)) {
      return false;
    }

    return true;
  }

  /**
   * Whether the participant has been removed. The invitation email helpers
   * return at their `participant_customer.is_active` guard before assembling
   * any recipient list, guardians included.
   */
  public participantIsRemoved(): boolean {
    return (this.waitlistEntry.participant_customer_is_active === false);
  }

  /** The zone the endpoint reads the deadline in: that of the roster the entry is invited to. */
  public deadlineTimeZone(): string {
    return this.selectedRoster ? this.selectedRoster.time_zone : this.waitlistEntry.time_zone;
  }

  public handleClearNewDeadline() {
    this.newDeadlineDate = '';
    this.newDeadlineTime = '';
  }

  /**
   * Whether a new deadline has been half-filled in. Both halves are needed,
   * and neither is required, so this is the one combination to block.
   */
  public newDeadlineIncomplete(): boolean {
    return (
      (!!this.newDeadlineDate && !this.newDeadlineTime) ||
      (!this.newDeadlineDate && !!this.newDeadlineTime)
    );
  }

  public submitDisabled(): boolean {
    return (
      (this.selectedRoster === null) ||
      this.newDeadlineIncomplete()
    );
  }

  public handleSendInvite() {
    this.loadingSubmit = true;
    this.errorMessage = '';

    this.scheduledEventService
      .inviteWaitlistEntryToRoster(
        this.waitlistEntry.id,
        this.selectedRoster.id,
        this.startDate,
        this.startDateCleared(),
        this.newDeadlineDate,
        this.newDeadlineTime,
        this.inviteNote,
        this.sendInviteToParentsAndGuardians
      )
      .then(() => {
        this.inviteSentCallback({
          text: (
            this.waitlistEntry.first_name + ' ' + this.waitlistEntry.last_name +
            (
              this.isResend()
                ? "'s invitation to join the roster was sent again."
                : ' successfully invited to join the roster.'
            )
          ),
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to send this invite.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingSubmit = false;
      });
  }

  private loadRosters() {
    this.loadingRosters = true;

    this.eventTypeService
      .getRosters(
        1,
        3000,
        {event_type_id: this.waitlistEntry.event_type_id}
      )
      .then(response => {
        this.rosters = response.results;

        // Preselect the roster the entry is already on, so a re-send goes to
        // the same roster the participant was originally invited to.
        if (this.waitlistEntry.roster) {
          for (const roster of this.rosters) {
            if (roster.id === this.waitlistEntry.roster.id) {
              this.selectedRoster = roster;
            }
          }
        }

        if ((this.selectedRoster === null) && (this.rosters.length === 1)) {
          this.selectedRoster = this.rosters[0];
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingRosters = false; });
  }

  /**
   * Whether a linked parent or guardian could receive this invitation. Only
   * needed when the entry has no address of its own, since ticking the
   * guardians box can then make the no-recipient warning wrong.
   */
  private loadGuardianEmailAvailability() {
    this.guardianWithEmailExists = null;

    if (!this.waitlistEntry.participant_customer_id) { return; }
    if (this.participantIsRemoved()) { return; }
    if (this.emailRecipients().length > 0) { return; }

    this.loadingRelations = true;

    // Increment the request count and save a local copy.
    this.relationsRequestCount++;
    const thisRequestCount = this.relationsRequestCount;

    this.customerService
      .getCustomerRelations(this.waitlistEntry.participant_customer_id)
      .then(result => {
        if (thisRequestCount !== this.relationsRequestCount) { return; }
        this.guardianWithEmailExists = result.results.some(isGuardianWithEmail);
      })
      .catch(() => {
        // Unknown: the warning stays silent rather than claim the unchecked.
        if (thisRequestCount !== this.relationsRequestCount) { return; }
        this.guardianWithEmailExists = null;
      })
      .finally(() => {
        if (thisRequestCount === this.relationsRequestCount) {
          this.loadingRelations = false;
        }
      });
  }

  /**
   * A membership cannot be owned by someone below the organization's minimum
   * age, so for those participants the invitation has to reach a guardian.
   */
  private checkParticipantAge() {
    const orgConfig = this.orgService.getOrganizationConfig();
    const minAge = orgConfig && orgConfig.membership_minimum_age;

    if (!minAge || !this.waitlistEntry.birthday) { return; }
    if (getAge(this.waitlistEntry.birthday) >= minAge) { return; }

    this.participantBelowMinAge = true;
    this.sendInviteToParentsAndGuardians = true;
  }
}
